import { useCallback } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useHydrateAtoms } from 'jotai/utils'

import { TextFieldInput, GridItem } from '@sequencehq/core-components'
import { lineItemGroupsAtom } from 'Invoices/atoms/invoiceEditing.atom'
import {
  Controls,
  FieldGrid,
  FieldLabel,
  SecondaryOptions,
  Title
} from '../../common/layout'
import { BaseConfig, CreateLineItemConfig, VariantConfig } from '../../types'
import {
  DescriptionField,
  LedgerIntegrationFields,
  PercentageRatesToggle,
  QuantityField,
  RateField
} from '../../common/fields'
import {
  descriptionAtom,
  descriptionEnabledAtom,
  externalIdsAtom,
  formStateAtom,
  INITIAL_STATE,
  quantityAtom,
  rateAtom,
  rateDisplayAtom,
  titleAtom
} from './state.atoms'
import { validateFormAtom, validationResultsAtom } from './validation.atoms'
import { setDefaultExternalIds, toLineItem } from '../../common/adapters'
import { isFieldInvalid } from '../../common/validation'
import { secondaryOptionsVisibleAtom } from '../../common/atoms'
import { servicePeriodStringToDates } from '../../common/utils/servicePeriodStringToDates'

type Props = CreateLineItemConfig &
  BaseConfig & {
    setLineItemEditor: (config: VariantConfig | null) => void
  }

export const CreateLineItem = (props: Props) => {
  useHydrateAtoms([
    [externalIdsAtom, setDefaultExternalIds(props.customerLedgerIntegrations)]
  ])

  const [title, setTitle] = useAtom(titleAtom)
  const [quantity, setQuantity] = useAtom(quantityAtom)
  const [rate, setRate] = useAtom(rateAtom)
  const [rateDisplay, setRateDisplay] = useAtom(rateDisplayAtom)

  const [descriptionEnabled, setDescriptionEnabled] = useAtom(
    descriptionEnabledAtom
  )
  const [description, setDescription] = useAtom(descriptionAtom)
  const [externalIds, setExternalIds] = useAtom(externalIdsAtom)

  const validationResults = useAtomValue(validationResultsAtom)
  const validateForm = useSetAtom(validateFormAtom)
  const [lineItemGroups, setLineItemGroups] = useAtom(lineItemGroupsAtom)
  const setFormState = useSetAtom(formStateAtom)
  const [secondaryOptionsVisible, setSecondaryOptionsVisible] = useAtom(
    secondaryOptionsVisibleAtom
  )

  const onSave = useCallback(
    ({ addAnother = false }: { addAnother?: boolean } = {}) => {
      const result = validateForm()

      if (!result || !lineItemGroups) return

      setLineItemGroups(
        lineItemGroups.map(group => {
          if (props.lineItemGroupId !== group.id) return group

          const firstLineItem = group.lineItems.at(0)

          return {
            ...group,
            lineItems: [
              ...group.lineItems,
              toLineItem({
                id: undefined,
                title,
                quantity,
                rate,
                rateDisplay,
                taxCategoryId: group.taxCategory?.id ?? '',
                descriptionEnabled,
                description,
                externalIds,
                /**
                 * Inherit these values from other line items in this group
                 */
                servicePeriod: firstLineItem?.servicePeriod
                  ? servicePeriodStringToDates(firstLineItem.servicePeriod)
                  : undefined,
                revenueRecognitionMethod:
                  firstLineItem?.revenueRecognitionMethod,
                revenueClassification: firstLineItem?.revenueClassification,
                productId: firstLineItem?.productId,
                priceId: undefined,
                creditGrantId: undefined
              })
            ]
          }
        })
      )

      if (addAnother) {
        setFormState(INITIAL_STATE)

        props.setLineItemEditor({
          variant: 'CREATE_LINE_ITEM',
          lineItemGroupId: props.lineItemGroupId,
          key: crypto.randomUUID()
        })

        return
      }

      props.onDismiss()
    },
    [
      props.onDismiss,
      props.lineItemGroupId,
      props.setLineItemEditor,
      validateForm,
      setLineItemGroups,
      lineItemGroups,
      setFormState,
      title,
      quantity,
      rate,
      rateDisplay,
      descriptionEnabled,
      description,
      externalIds
    ]
  )

  return (
    <>
      <Title>Add sub item</Title>

      <FieldGrid>
        <GridItem gridColumn="span 2">
          <FieldLabel label="Name" validationErrors={validationResults?.title}>
            <TextFieldInput
              placeholder="Name"
              value={title}
              onChange={e => setTitle(e.target.value)}
              autoFocus
              isInvalid={isFieldInvalid(validationResults?.title)}
              data-1p-ignore
              data-lpignore
            />
          </FieldLabel>
        </GridItem>
        <GridItem>
          <FieldLabel label="Qty" textAlign="right">
            <QuantityField
              value={quantity}
              onChange={setQuantity}
              rateDisplay={rateDisplay}
              isInvalid={isFieldInvalid(validationResults?.quantity)}
            />
          </FieldLabel>
        </GridItem>
        <GridItem>
          <FieldLabel label="Rate" textAlign="right">
            <RateField
              value={rate}
              onChange={setRate}
              rateDisplay={rateDisplay}
              isInvalid={isFieldInvalid(validationResults?.rate)}
            />
          </FieldLabel>
        </GridItem>
      </FieldGrid>

      <SecondaryOptions
        isVisible={secondaryOptionsVisible}
        setIsVisible={setSecondaryOptionsVisible}
        toggles={[
          <PercentageRatesToggle
            key="percentage-rates-toggle"
            rateDisplay={rateDisplay}
            setRateDisplay={setRateDisplay}
          />
        ]}
      >
        <SecondaryOptions.Group>
          <SecondaryOptions.ToggleField
            label="Description"
            isVisible={descriptionEnabled}
            setIsVisible={setDescriptionEnabled}
          >
            <DescriptionField
              description={description}
              setDescription={setDescription}
              validationErrors={validationResults?.description}
            />
          </SecondaryOptions.ToggleField>
        </SecondaryOptions.Group>

        <SecondaryOptions.Group>
          <LedgerIntegrationFields
            customerLedgerIntegrations={props.customerLedgerIntegrations}
            externalIds={externalIds}
            setExternalIds={setExternalIds}
            validationResults={validationResults?.externalIds ?? []}
          />
        </SecondaryOptions.Group>
      </SecondaryOptions>

      <Controls>
        <Controls.Cancel onClick={props.onDismiss} />
        <Controls.SaveAndAddAnother
          onClick={() => onSave({ addAnother: true })}
        />
        <Controls.Confirm
          onClick={onSave}
          keyboardShortcut={['Mod', 'Enter']}
        />
      </Controls>
    </>
  )
}
